/**
 * SmartSearchPanels
 * -----------------
 * Drop-in panels used by the mobile Remote Control AND the desktop admin
 * Display Manager to search Bible passages and Hymns with:
 *   • predictive book typeahead (Bible)
 *   • voice search via Web Speech API (Bible)
 *   • partial-word fuzzy hymn search across title + lyrics + both languages
 *   • preview-first, then "Show Live" flow
 *   • Next / Previous verse shortcut buttons (Bible) that drive the live
 *     display when it's active
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mic, MicOff, Search, ChevronRight, ChevronLeft, Play, Eye, Loader2, BookOpen, Music } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '')}/api`;

// ---------------------------------------------------------------------------
// Bible book list — canonical names + common abbreviations so "1 sam", "psalm",
// "ps", "revelations" all match.
// ---------------------------------------------------------------------------
const BOOKS = [
  ['Genesis', 'gen'], ['Exodus', 'exo', 'ex'], ['Leviticus', 'lev'],
  ['Numbers', 'num'], ['Deuteronomy', 'deut', 'deu'], ['Joshua', 'josh'],
  ['Judges', 'judg'], ['Ruth'], ['1 Samuel', '1sam', '1 sam'],
  ['2 Samuel', '2sam', '2 sam'], ['1 Kings', '1kings', '1 kgs'],
  ['2 Kings', '2kings', '2 kgs'], ['1 Chronicles', '1chr', '1 chr'],
  ['2 Chronicles', '2chr', '2 chr'], ['Ezra'], ['Nehemiah', 'neh'],
  ['Esther', 'est'], ['Job'], ['Psalms', 'psalm', 'ps'], ['Proverbs', 'prov', 'pro'],
  ['Ecclesiastes', 'eccl', 'ecc'], ['Song of Solomon', 'song'],
  ['Isaiah', 'isa'], ['Jeremiah', 'jer'], ['Lamentations', 'lam'],
  ['Ezekiel', 'ezek', 'eze'], ['Daniel', 'dan'], ['Hosea', 'hos'],
  ['Joel'], ['Amos'], ['Obadiah', 'obad'], ['Jonah'], ['Micah', 'mic'],
  ['Nahum', 'nah'], ['Habakkuk', 'hab'], ['Zephaniah', 'zeph'],
  ['Haggai', 'hag'], ['Zechariah', 'zech'], ['Malachi', 'mal'],
  ['Matthew', 'matt', 'mat'], ['Mark', 'mk'], ['Luke', 'lk'], ['John', 'jn'],
  ['Acts'], ['Romans', 'rom'], ['1 Corinthians', '1cor', '1 cor'],
  ['2 Corinthians', '2cor', '2 cor'], ['Galatians', 'gal'],
  ['Ephesians', 'eph'], ['Philippians', 'phil', 'phi'],
  ['Colossians', 'col'], ['1 Thessalonians', '1thes', '1 thes'],
  ['2 Thessalonians', '2thes', '2 thes'], ['1 Timothy', '1tim', '1 tim'],
  ['2 Timothy', '2tim', '2 tim'], ['Titus'], ['Philemon', 'phlm'],
  ['Hebrews', 'heb'], ['James', 'jas'], ['1 Peter', '1pet', '1 pet'],
  ['2 Peter', '2pet', '2 pet'], ['1 John', '1jn', '1 john'],
  ['2 John', '2jn', '2 john'], ['3 John', '3jn', '3 john'],
  ['Jude'], ['Revelation', 'rev', 'revelations'],
];

const BOOK_NAMES = BOOKS.map(b => b[0]);

/** Case-insensitive book lookup that accepts abbreviations and raw names. */
function resolveBook(input) {
  const s = (input || '').trim().toLowerCase();
  if (!s) return null;
  for (const entry of BOOKS) {
    for (const alias of entry) {
      if (alias.toLowerCase() === s) return entry[0];
    }
  }
  // partial-prefix fallback
  for (const entry of BOOKS) {
    if (entry[0].toLowerCase().startsWith(s)) return entry[0];
  }
  return null;
}

/** Parse "Genesis 1 verse 1" / "john 3:16" / "psalm 23" into {book, chapter, verse}. */
export function parseBibleSpeech(text) {
  if (!text) return null;
  const cleaned = text
    .replace(/\bverses?\b/gi, ':')
    .replace(/\bchapter\b/gi, '')
    .replace(/\s+-\s+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  // book can be "1 samuel" etc., so match greedily up to the first digit group
  const m = cleaned.match(/^(.*?)\s*(\d+)(?:\s*[:.\s]\s*(\d+)(?:\s*[-–]\s*(\d+))?)?$/i);
  if (!m) return null;
  const book = resolveBook(m[1]);
  if (!book) return null;
  return {
    book,
    chapter: parseInt(m[2], 10) || 1,
    verse: m[3] ? parseInt(m[3], 10) : null,
    verseEnd: m[4] ? parseInt(m[4], 10) : null,
    raw: cleaned,
  };
}

// ---------------------------------------------------------------------------
// Web Speech API hook — degrades silently if unsupported (iOS Safari etc.)
// ---------------------------------------------------------------------------
function useSpeechRecognition(onResult) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return undefined;
    setSupported(true);
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setListening(false);
      onResult?.(transcript);
    };
    rec.onerror = (e) => { setError(e.error || 'speech error'); setListening(false); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    return () => { try { rec.abort(); } catch (_) {} };
  }, [onResult]);
  const start = () => {
    if (!recRef.current) return;
    try { setError(''); recRef.current.start(); setListening(true); } catch (_) {}
  };
  const stop = () => { try { recRef.current?.stop(); } catch (_) {} };
  return { listening, supported, error, start, stop };
}

// ---------------------------------------------------------------------------
// Bible Smart Search
// ---------------------------------------------------------------------------
export const BibleSmartSearch = ({ compact = false, onShown }) => {
  const [book, setBook] = useState('');
  const [chapter, setChapter] = useState('');
  const [verse, setVerse] = useState('');
  const [language, setLanguage] = useState('english');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState('');

  const bookSuggestions = useMemo(() => {
    if (!book) return [];
    const q = book.trim().toLowerCase();
    return BOOK_NAMES.filter(b => b.toLowerCase().startsWith(q)).slice(0, 6);
  }, [book]);

  const handleVoice = useCallback((t) => {
    setTranscript(t);
    const parsed = parseBibleSpeech(t);
    if (parsed) {
      setBook(parsed.book);
      setChapter(String(parsed.chapter));
      if (parsed.verse) setVerse(String(parsed.verse) + (parsed.verseEnd ? `-${parsed.verseEnd}` : ''));
      // auto-fetch preview
      setTimeout(() => runPreview(parsed.book, parsed.chapter, parsed.verse
        ? (parsed.verseEnd ? `${parsed.verse}-${parsed.verseEnd}` : String(parsed.verse))
        : ''), 50);
    }
  }, []); // eslint-disable-line

  const voice = useSpeechRecognition(handleVoice);

  const buildReference = (b = book, c = chapter, v = verse) => {
    if (!b || !c) return '';
    return v ? `${b} ${c}:${v}` : `${b} ${c}`;
  };

  const runPreview = async (b = book, c = chapter, v = verse) => {
    const reference = buildReference(b, c, v);
    if (!reference) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/display/show/bible`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference, language, preview: true }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPreview({ reference, ...data });
    } catch (e) {
      setPreview({ reference, error: String(e.message || e) });
    } finally { setLoading(false); }
  };

  const goLive = async () => {
    const reference = buildReference();
    if (!reference) return;
    setLoading(true);
    try {
      await fetch(`${API}/display/show/bible`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference, language }),
      });
      onShown?.({ type: 'bible', reference });
    } catch (_) {} finally { setLoading(false); }
  };

  const step = async (direction) => {
    // Rely on existing /bible/verse/{i} endpoint — index unknown client-side
    // so this derives the next/prev relative to the currently-shown verse.
    try {
      const cur = await fetch(`${API}/display/current`).then(r => r.json()).catch(() => ({}));
      if (cur?.display_type !== 'bible') return;
      const idx = cur.content?.current_verse_index ?? 0;
      const total = cur.content?.total_verses ?? 1;
      const next = direction === 'next' ? Math.min(idx + 1, total - 1) : Math.max(idx - 1, 0);
      await fetch(`${API}/display/bible/verse/${next}`, { method: 'POST' });
    } catch (_) {}
  };

  return (
    <div className={`rounded-2xl border ${compact ? 'p-3 bg-white/10 border-white/15 text-white' : 'p-5 bg-white border-slate-200 text-slate-900'}`} data-testid="bible-smart-search">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className={`w-4 h-4 ${compact ? 'text-blue-300' : 'text-indigo-600'}`} />
        <h3 className="font-bold text-sm">Smart Bible Search</h3>
        {voice.supported && (
          <button
            onClick={voice.listening ? voice.stop : voice.start}
            data-testid="bible-voice-btn"
            className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${voice.listening
              ? 'bg-red-500 text-white animate-pulse'
              : (compact ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-indigo-600 text-white hover:bg-indigo-500')}`}
            title={voice.listening ? 'Listening… tap to stop' : 'Tap and say e.g. "John 3 verse 16"'}
          >
            {voice.listening ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
            {voice.listening ? 'Listening…' : 'Voice'}
          </button>
        )}
      </div>
      {transcript && (
        <p className={`text-[11px] mb-2 ${compact ? 'text-white/70' : 'text-slate-500'}`}>
          Heard: <em>"{transcript}"</em>
        </p>
      )}

      <div className="grid grid-cols-12 gap-2 mb-2">
        <div className="col-span-6 relative">
          <input
            type="text" placeholder="Book (e.g. Gen)"
            value={book} onChange={(e) => setBook(e.target.value)}
            data-testid="bible-book-input"
            className={`w-full px-3 py-2 rounded-lg text-sm ${compact ? 'bg-white/15 border border-white/20 text-white placeholder-white/40' : 'bg-slate-50 border border-slate-300'}`}
          />
          {bookSuggestions.length > 0 && bookSuggestions[0].toLowerCase() !== book.toLowerCase() && (
            <ul className={`absolute z-10 left-0 right-0 mt-1 rounded-lg shadow-lg overflow-hidden ${compact ? 'bg-slate-900 border border-white/20' : 'bg-white border border-slate-200'}`}>
              {bookSuggestions.map(b => (
                <li key={b}>
                  <button
                    onClick={() => setBook(b)}
                    data-testid={`bible-book-suggest-${b.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`w-full text-left px-3 py-1.5 text-sm ${compact ? 'text-white hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100'}`}
                  >{b}</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input
          type="text" placeholder="Ch" inputMode="numeric"
          value={chapter} onChange={(e) => setChapter(e.target.value.replace(/\D/g, ''))}
          data-testid="bible-chapter-input"
          className={`col-span-3 px-3 py-2 rounded-lg text-sm text-center ${compact ? 'bg-white/15 border border-white/20 text-white placeholder-white/40' : 'bg-slate-50 border border-slate-300'}`}
        />
        <input
          type="text" placeholder="Vs"
          value={verse} onChange={(e) => setVerse(e.target.value.replace(/[^0-9\-]/g, ''))}
          data-testid="bible-verse-input"
          className={`col-span-3 px-3 py-2 rounded-lg text-sm text-center ${compact ? 'bg-white/15 border border-white/20 text-white placeholder-white/40' : 'bg-slate-50 border border-slate-300'}`}
        />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <select value={language} onChange={(e) => setLanguage(e.target.value)}
          data-testid="bible-language-select"
          className={`text-xs rounded-lg px-2 py-1.5 ${compact ? 'bg-white/15 border border-white/20 text-white' : 'bg-slate-50 border border-slate-300'}`}>
          <option value="english">English</option>
          <option value="yoruba">Yoruba</option>
          <option value="both">Both</option>
        </select>
        <button
          onClick={() => runPreview()}
          disabled={!book || !chapter || loading}
          data-testid="bible-preview-btn"
          className={`ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${compact ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'} disabled:opacity-50`}
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview
        </button>
        <button
          onClick={goLive}
          disabled={!book || !chapter || loading}
          data-testid="bible-go-live-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400 disabled:opacity-50"
        >
          <Play className="w-3 h-3 fill-white" /> Go Live
        </button>
      </div>

      {preview && !preview.error && (
        <div className={`rounded-lg p-3 text-sm mb-2 max-h-40 overflow-y-auto ${compact ? 'bg-black/30 text-white/90' : 'bg-indigo-50 text-slate-800'}`} data-testid="bible-preview-box">
          <p className="text-xs font-bold mb-1">{preview.reference} · {preview.total_verses || 1} verse{(preview.total_verses || 1) > 1 ? 's' : ''}</p>
          <p className="whitespace-pre-wrap">{preview.first_text || '(no text)'}</p>
        </div>
      )}
      {preview?.error && (
        <p className="text-xs text-red-400 mb-2">Preview failed: {preview.error}</p>
      )}

      <div className="flex gap-2">
        <button onClick={() => step('prev')}
          data-testid="bible-step-prev"
          className={`flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold ${compact ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
          <ChevronLeft className="w-3 h-3" /> Prev verse
        </button>
        <button onClick={() => step('next')}
          data-testid="bible-step-next"
          className={`flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold ${compact ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
          Next verse <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Hymn Smart Search
// ---------------------------------------------------------------------------
export const HymnSmartSearch = ({ compact = false, onShown }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewHymn, setPreviewHymn] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 1) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/hymns/search?q=${encodeURIComponent(q)}&language=all&limit=20`);        if (!res.ok) throw new Error();
        const d = await res.json();
        // Deduplicate by hymn number — prefer English title in the list row
        const byNum = {};
        (d.results || []).forEach(h => {
          const n = h.number;
          if (!byNum[n]) byNum[n] = h;
          else if (h.language === 'english') byNum[n] = h;
        });
        setResults(Object.values(byNum).slice(0, 15));
      } catch (_) { setResults([]); }
      finally { setLoading(false); }
    }, 120);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const loadPreview = async (hymnNumber, language = 'english') => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/display/show/hymn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hymn_number: hymnNumber, language, preview: true }),
      });
      const d = await res.json();
      setPreviewHymn(d);
    } catch (_) {} finally { setLoading(false); }
  };

  const goLive = async () => {
    if (!previewHymn?.hymn_number) return;
    await fetch(`${API}/display/show/hymn`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hymn_number: previewHymn.hymn_number, language: previewHymn.language || 'english' }),
    });
    onShown?.({ type: 'hymn', number: previewHymn.hymn_number });
  };

  return (
    <div className={`rounded-2xl border ${compact ? 'p-3 bg-white/10 border-white/15 text-white' : 'p-5 bg-white border-slate-200 text-slate-900'}`} data-testid="hymn-smart-search">
      <div className="flex items-center gap-2 mb-3">
        <Music className={`w-4 h-4 ${compact ? 'text-amber-300' : 'text-amber-600'}`} />
        <h3 className="font-bold text-sm">Smart Hymn Search</h3>
        {loading && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60" />
        <input
          type="text" placeholder="Type title, number, or any lyric (e.g. Yin Soke)"
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="hymn-search-input"
          className={`w-full pl-8 pr-3 py-2 rounded-lg text-sm ${compact ? 'bg-white/15 border border-white/20 text-white placeholder-white/40' : 'bg-slate-50 border border-slate-300'}`}
        />
      </div>

      {results.length > 0 && (
        <ul className={`rounded-lg max-h-48 overflow-y-auto mb-2 ${compact ? 'bg-black/20' : 'bg-slate-50'}`} data-testid="hymn-search-results">
          {results.map(h => (
            <li key={`${h.number}-${h.language}`}>
              <button
                onClick={() => loadPreview(h.number, h.language || 'english')}
                data-testid={`hymn-result-${h.number}`}
                className={`w-full text-left px-3 py-2 text-sm ${compact ? 'text-white hover:bg-white/10 border-b border-white/5' : 'text-slate-700 hover:bg-slate-100 border-b border-slate-200'}`}
              >
                <span className="font-mono text-xs opacity-70 mr-2">#{h.number}</span>
                <span className="font-semibold">{h.title}</span>
                {h.first_line && <span className={`ml-2 text-xs italic ${compact ? 'text-white/50' : 'text-slate-500'}`}>— {h.first_line}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {previewHymn && (
        <div className={`rounded-lg p-3 text-sm ${compact ? 'bg-black/30 text-white/90' : 'bg-amber-50 text-slate-800'}`} data-testid="hymn-preview-box">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold">#{previewHymn.hymn_number} · {previewHymn.title}</p>
            <button onClick={goLive}
              data-testid="hymn-go-live-btn"
              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400">
              <Play className="w-3 h-3 fill-white" /> Go Live
            </button>
          </div>
          <div className="max-h-32 overflow-y-auto whitespace-pre-wrap text-xs opacity-90">
            {(previewHymn.verses?.[0]?.text) ||
              (Array.isArray(previewHymn.verses) && previewHymn.verses[0]) ||
              '(no preview text)'}
          </div>
        </div>
      )}
    </div>
  );
};

export default BibleSmartSearch;
